'use client';
import React, { useState, useCallback } from 'react';
import { Search, MapPin, Truck, ArrowRight, ChevronRight, Heart, Clock, Package, Star, Shield } from 'lucide-react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';

interface TransportService {
  id: string; name: string; type: string; provider: string; location: string;
  price_from: number; rating: number; reviews: number; image_url: string;
  is_featured: boolean; capacity: string; availability: string; verified: boolean; tags: string[];
}

const SERVICES: TransportService[] = [
  { id: '1', name: 'Nairobi–Mombasa Express', type: 'Long Distance', provider: 'Modern Coast', location: 'Nairobi', price_from: 1200, rating: 4.8, reviews: 892, image_url: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957', is_featured: true, capacity: '50 seats', availability: 'Daily 7AM, 9PM', verified: true, tags: ['AC', 'WiFi', 'Reclining'] },
  { id: '2', name: 'Nairobi–Kisumu Shuttle', type: 'Shuttle', provider: 'Easy Coach', location: 'Nairobi', price_from: 900, rating: 4.7, reviews: 445, image_url: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957', is_featured: false, capacity: '14 seats', availability: 'Daily 6AM–6PM', verified: true, tags: ['Shuttle', 'Comfortable', 'On-time'] },
  { id: '3', name: 'Airport Transfer (CBD)', type: 'Airport', provider: 'Uber Black', location: 'Nairobi', price_from: 1500, rating: 4.9, reviews: 234, image_url: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d', is_featured: true, capacity: '4 passengers', availability: '24/7', verified: true, tags: ['Airport', 'Premium', 'Meet & Greet'] },
  { id: '4', name: 'Cargo Delivery (Nairobi)', type: 'Cargo', provider: 'G4S Logistics', location: 'Nairobi', price_from: 500, rating: 4.6, reviews: 178, image_url: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7', is_featured: false, capacity: 'Up to 500kg', availability: 'Mon–Sat 8AM–6PM', verified: true, tags: ['Cargo', 'Same-day', 'Tracked'] },
  { id: '5', name: 'Matatu Hire (Full Day)', type: 'Hire', provider: 'Safari Matatu', location: 'Mombasa', price_from: 8000, rating: 4.5, reviews: 67, image_url: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957', is_featured: false, capacity: '14 seats', availability: 'By booking', verified: false, tags: ['Hire', 'Events', 'Flexible'] },
  { id: '6', name: 'Boda Boda Delivery', type: 'Last Mile', provider: 'Sendy Kenya', location: 'Nairobi', price_from: 150, rating: 4.4, reviews: 1240, image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64', is_featured: false, capacity: 'Small parcels', availability: '7AM–10PM', verified: true, tags: ['Fast', 'Affordable', 'Tracked'] },
];

const TYPES = ['All Types', 'Long Distance', 'Shuttle', 'Airport', 'Cargo', 'Hire', 'Last Mile'];
const LOCATIONS = ['All Locations', 'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru'];

export default function TransportLandingPage() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('All Types');
  const [location, setLocation] = useState('All Locations');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const filtered = SERVICES.filter(s => {
    const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.provider.toLowerCase().includes(search.toLowerCase());
    const matchType = type === 'All Types' || s.type === type;
    const matchLoc = location === 'All Locations' || s.location === location;
    return matchSearch && matchType && matchLoc;
  });

  const toggleSave = useCallback((id: string) => {
    setSavedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen" style={{ background: '#070C1A' }}>
        {/* Hero */}
        <div className="relative px-4 pt-8 pb-10 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #001a08 0%, #0a1a0d 50%, #070C1A 100%)' }}>
          <div className="absolute inset-0 opacity-20"
            style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #00C853 0%, transparent 50%), radial-gradient(circle at 75% 30%, #00BCD4 0%, transparent 50%)' }} />
          <div className="relative max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-600 mb-4"
              style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}>
              <Truck size={12} /> {(2400).toLocaleString()} Transport Services
            </div>
            <h1 className="text-3xl font-800 text-white mb-3 leading-tight">
              Book <span style={{ color: '#00C853' }}>Transport</span> Across Kenya
            </h1>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Buses, shuttles, cargo, airport transfers and last-mile delivery. Book in seconds.
            </p>
            <div className="flex gap-2 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search routes or providers…"
                  className="w-full pl-9 pr-3 py-3.5 rounded-2xl text-sm outline-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <div className="relative">
                <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.4)' }} />
                <select value={location} onChange={e => setLocation(e.target.value)}
                  className="pl-8 pr-3 py-3.5 rounded-2xl text-sm outline-none appearance-none"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                  {LOCATIONS.map(l => <option key={l} value={l} style={{ background: '#0D1526' }}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Type pills */}
        <div className="px-4 py-3 sticky top-0 z-20"
          style={{ background: 'rgba(7,12,26,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto pb-1">
            {TYPES.map(t => (
              <button key={t} onClick={() => setType(t)}
                className="px-3 py-2 rounded-xl text-xs font-600 flex-shrink-0 transition-all"
                style={{ background: type === t ? 'rgba(0,200,83,0.15)' : 'rgba(255,255,255,0.04)', color: type === t ? '#00C853' : 'rgba(255,255,255,0.55)', border: `1px solid ${type === t ? 'rgba(0,200,83,0.3)' : 'rgba(255,255,255,0.08)'}` }}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 pt-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <span style={{ color: '#00C853', fontWeight: 700 }}>{filtered.length}</span> services found
            </span>
            <Link href="/login" className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-700"
              style={{ background: '#00C853', color: '#000' }}>
              List Your Service <ArrowRight size={12} />
            </Link>
          </div>

          <div className="flex flex-col gap-4">
            {filtered.map(s => (
              <div key={s.id} className="p-4 rounded-2xl"
                style={{ background: s.is_featured ? 'rgba(0,200,83,0.04)' : 'rgba(255,255,255,0.03)', border: `1px solid ${s.is_featured ? 'rgba(0,200,83,0.2)' : 'rgba(255,255,255,0.07)'}` }}>
                <div className="flex gap-3">
                  <img src={s.image_url} alt={`${s.name} transport service by ${s.provider}`}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-700 text-white">{s.name}</h3>
                          {s.is_featured && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md font-700"
                              style={{ background: 'rgba(255,179,0,0.15)', color: '#FFB300' }}>Featured</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{s.provider}</p>
                          {s.verified && <Shield size={9} style={{ color: '#00C853' }} />}
                        </div>
                      </div>
                      <button onClick={() => toggleSave(s.id)} className="flex-shrink-0 p-1.5 rounded-lg"
                        style={{ background: savedIds.has(s.id) ? 'rgba(255,64,129,0.15)' : 'rgba(255,255,255,0.05)' }}>
                        <Heart size={13} fill={savedIds.has(s.id) ? '#FF4081' : 'none'} style={{ color: savedIds.has(s.id) ? '#FF4081' : 'rgba(255,255,255,0.4)' }} />
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <div className="flex items-center gap-1">
                        <Package size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.capacity}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={10} style={{ color: 'rgba(255,255,255,0.35)' }} />
                        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>{s.availability}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={10} fill="#FFB300" style={{ color: '#FFB300' }} />
                        <span className="text-xs font-700" style={{ color: '#FFB300' }}>{s.rating}</span>
                        <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>({s.reviews})</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.tags.map(t => (
                        <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-md font-600"
                          style={{ background: 'rgba(0,200,83,0.1)', color: '#69F0AE' }}>{t}</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm font-800" style={{ color: '#00C853' }}>from KES {s.price_from.toLocaleString()}</span>
                      <Link href="/login" className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-700"
                        style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.25)' }}>
                        Book Now <ChevronRight size={11} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 rounded-3xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,200,83,0.1), rgba(0,188,212,0.1))', border: '1px solid rgba(0,200,83,0.2)' }}>
            <h3 className="text-lg font-800 text-white mb-2">See All {(2400).toLocaleString()} Services</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>Sign up to book, track, and manage all your transport needs.</p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
                style={{ background: '#00C853', color: '#000' }}>
                <Truck size={16} /> Book Transport
              </Link>
              <Link href="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-700 text-sm"
                style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}>
                List Your Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
